import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { testimonialService } from '../../../services/testimonialService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TestimonialManager = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    studentName: '',
    studentProgram: '',
    studentYear: '',
    rating: 5,
    content: '',
    studentAvatar: '',
    isFeatured: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      fetchTestimonials();
    }
  }, [user]);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const data = await testimonialService?.getInstituteTestimonials(user?.id);
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast?.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.studentName?.trim()) {
      errors.studentName = 'Student name is required';
    }
    
    if (!formData?.studentProgram?.trim()) {
      errors.studentProgram = 'Program is required';
    }
    
    if (!formData?.studentYear?.trim()) {
      errors.studentYear = 'Year is required';
    }
    
    if (!formData?.content?.trim()) {
      errors.content = 'Testimonial content is required';
    } else if (formData?.content?.trim()?.length < 20) {
      errors.content = 'Content must be at least 20 characters';
    }
    
    if (formData?.rating < 1 || formData?.rating > 5) {
      errors.rating = 'Rating must be between 1 and 5';
    }
    
    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleOpenAddModal = () => {
    setFormData({
      studentName: '',
      studentProgram: '',
      studentYear: '',
      rating: 5,
      content: '',
      studentAvatar: '',
      isFeatured: false
    });
    setFormErrors({});
    setEditingTestimonial(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (testimonial) => {
    setFormData({
      studentName: testimonial?.studentName || '',
      studentProgram: testimonial?.studentProgram || '',
      studentYear: testimonial?.studentYear || '',
      rating: testimonial?.rating || 5,
      content: testimonial?.content || '',
      studentAvatar: testimonial?.studentAvatar || '',
      isFeatured: testimonial?.isFeatured || false
    });
    setFormErrors({});
    setEditingTestimonial(testimonial);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingTestimonial(null);
    setFormData({
      studentName: '',
      studentProgram: '',
      studentYear: '',
      rating: 5,
      content: '',
      studentAvatar: '',
      isFeatured: false
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      toast?.error('Please fix all validation errors');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingTestimonial) {
        await testimonialService?.updateTestimonial(editingTestimonial?.id, {
          ...formData,
          instituteId: user?.id
        });
        toast?.success('Testimonial updated successfully');
      } else {
        await testimonialService?.createTestimonial({
          ...formData,
          instituteId: user?.id
        });
        toast?.success('Testimonial added successfully');
      }
      
      handleCloseModal();
      fetchTestimonials();
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast?.error(error?.message || 'Failed to save testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }

    try {
      await testimonialService?.deleteTestimonial(testimonialId);
      toast?.success('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast?.error('Failed to delete testimonial');
    }
  };

  const handleToggleFeatured = async (testimonial) => {
    try {
      await testimonialService?.updateTestimonial(testimonial?.id, {
        isFeatured: !testimonial?.isFeatured
      });
      toast?.success(`Testimonial ${!testimonial?.isFeatured ? 'featured' : 'unfeatured'} successfully`);
      fetchTestimonials();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast?.error('Failed to update testimonial');
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">Loading testimonials...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground">Student Testimonials</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage testimonials displayed on your virtual booth</p>
        </div>
        <Button onClick={handleOpenAddModal} variant="default">
          <Icon name="Plus" size={18} className="mr-2" />
          Add Testimonial
        </Button>
      </div>
      {testimonials?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No testimonials yet</p>
          <Button onClick={handleOpenAddModal} variant="outline">
            Add Your First Testimonial
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials?.map((testimonial) => (
            <div key={testimonial?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    {testimonial?.studentAvatar ? (
                      <img src={testimonial?.studentAvatar} alt={testimonial?.studentName} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <Icon name="User" size={24} className="text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{testimonial?.studentName}</h3>
                      {testimonial?.isFeatured && (
                        <span className="px-2 py-0.5 bg-warning/10 text-warning text-xs rounded-full flex items-center gap-1">
                          <Icon name="Star" size={12} className="fill-current" />
                          Featured
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {testimonial?.studentProgram} • Class of {testimonial?.studentYear}
                    </p>
                    <div className="flex items-center gap-1 mb-2">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={14}
                          className={i < testimonial?.rating ? 'text-warning fill-current' : 'text-muted-foreground'}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-foreground line-clamp-2">{testimonial?.content}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleFeatured(testimonial)}
                    className="p-2 hover:bg-muted rounded-lg transition-smooth"
                    title={testimonial?.isFeatured ? 'Unfeature' : 'Feature'}
                  >
                    <Icon name="Star" size={18} className={testimonial?.isFeatured ? 'text-warning fill-current' : 'text-muted-foreground'} />
                  </button>
                  <button
                    onClick={() => handleOpenEditModal(testimonial)}
                    className="p-2 hover:bg-muted rounded-lg transition-smooth"
                    title="Edit"
                  >
                    <Icon name="Edit" size={18} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial?.id)}
                    className="p-2 hover:bg-destructive/10 rounded-lg transition-smooth"
                    title="Delete"
                  >
                    <Icon name="Trash2" size={18} className="text-destructive" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-heading font-bold text-foreground">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h3>
              <button onClick={handleCloseModal} className="p-2 hover:bg-muted rounded-lg transition-smooth">
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Student Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  value={formData?.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="Enter student name"
                />
                {formErrors?.studentName && (
                  <p className="text-sm text-destructive mt-1">{formErrors?.studentName}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Program <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData?.studentProgram}
                    onChange={(e) => setFormData({ ...formData, studentProgram: e?.target?.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    placeholder="e.g., Computer Science"
                  />
                  {formErrors?.studentProgram && (
                    <p className="text-sm text-destructive mt-1">{formErrors?.studentProgram}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Year <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData?.studentYear}
                    onChange={(e) => setFormData({ ...formData, studentYear: e?.target?.value })}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                    placeholder="e.g., 2023"
                  />
                  {formErrors?.studentYear && (
                    <p className="text-sm text-destructive mt-1">{formErrors?.studentYear}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Rating <span className="text-destructive">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Icon
                        name="Star"
                        size={24}
                        className={star <= formData?.rating ? 'text-warning fill-current' : 'text-muted-foreground'}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-sm text-muted-foreground">({formData?.rating}/5)</span>
                </div>
                {formErrors?.rating && (
                  <p className="text-sm text-destructive mt-1">{formErrors?.rating}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Testimonial Content <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={formData?.content}
                  onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground resize-none"
                  placeholder="Enter testimonial content (minimum 20 characters)"
                />
                <div className="flex items-center justify-between mt-1">
                  {formErrors?.content && (
                    <p className="text-sm text-destructive">{formErrors?.content}</p>
                  )}
                  <p className="text-xs text-muted-foreground ml-auto">{formData?.content?.length} characters</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Student Avatar URL (Optional)
                </label>
                <input
                  type="url"
                  value={formData?.studentAvatar}
                  onChange={(e) => setFormData({ ...formData, studentAvatar: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  placeholder="https://example.com/avatar.jpg"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isFeatured"
                  checked={formData?.isFeatured}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e?.target?.checked })}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                />
                <label htmlFor="isFeatured" className="text-sm text-foreground cursor-pointer">
                  Mark as featured (will be displayed prominently)
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" onClick={handleCloseModal} variant="outline" disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Icon name="Loader2" size={18} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Icon name="Check" size={18} className="mr-2" />
                      {editingTestimonial ? 'Update' : 'Add'} Testimonial
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialManager;